/**
 * Airports the user added because the built-in catalogue did not have them.
 *
 * Stored per browser rather than on the server, deliberately: the route itself
 * only ever persists a three-letter code, and everything here — the city and
 * country — is a label for that code. Nothing about the fare, the signal or the
 * history depends on it. That keeps the feature to one file with no migration,
 * no endpoint and no way to get out of sync with the backend.
 *
 * The trade-off is real and worth knowing: a custom airport does not follow the
 * user to another device, and a route created with one shows a bare code
 * elsewhere. Moving these server-side would mean a table and two endpoints; it
 * is the right call only once people are adding them on more than one machine.
 */

const KEY = 'genie.depart.customAirports';
const listeners = new Set();

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((a) => a && /^[A-Z]{3}$/.test(a.code)) : [];
  } catch {
    // Corrupt or unreadable storage must not take the picker down with it.
    return [];
  }
}

function write(list) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* quota or private mode */ }
  listeners.forEach((fn) => fn(list));
}

export function getCustomAirports() {
  return read();
}

/** @returns {{ok: true, airport} | {ok: false, error: string}} */
export function addCustomAirport({ code, city, country }) {
  const c = String(code || '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(c)) return { ok: false, error: 'Code must be three letters.' };
  const cityName = String(city || '').trim();
  if (!cityName) return { ok: false, error: 'Give it a city or airport name.' };
  const countryName = String(country || '').trim() || 'Added by you';

  const list = read();
  const airport = { code: c, city: cityName, country: countryName, custom: true };
  // Re-adding an existing code updates its labels rather than duplicating it.
  const next = [...list.filter((a) => a.code !== c), airport].sort((a, b) => a.city.localeCompare(b.city));
  write(next);
  return { ok: true, airport };
}

export function removeCustomAirport(code) {
  write(read().filter((a) => a.code !== String(code || '').toUpperCase()));
}

/** Subscribe to changes so an open picker updates without a remount. */
export function onCustomAirportsChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
